import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { openConfirmation } from '@/lib/invoice';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';

// All countries served — Israel is intentionally excluded.
const COUNTRIES = [
  'Saudi Arabia','United Arab Emirates','Qatar','Bahrain','Kuwait','Oman',
  'Egypt','Jordan','Lebanon','Iraq','Morocco','Tunisia','Algeria','Libya','Sudan','Yemen',
  'United States','Canada','Mexico','Brazil','Argentina','Chile','Colombia',
  'United Kingdom','Ireland','France','Germany','Spain','Italy','Portugal','Netherlands',
  'Belgium','Sweden','Norway','Denmark','Finland','Switzerland','Austria','Greece','Poland','Czechia','Turkey',
  'Australia','New Zealand','Japan','South Korea','Singapore','Malaysia','Thailand','Vietnam','Indonesia','Philippines','India','Pakistan','Bangladesh',
  'South Africa','Nigeria','Kenya','Ghana','Ethiopia',
].sort((a, b) => a.localeCompare(b));

const Checkout = () => {
  const { cart, refresh } = useCart();
  const { format } = useCurrency();
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({
    name: '', line1: '', city: '', state: '', zip: '', country: 'Saudi Arabia', phone: '',
  });
  const storeOpen = settings.store_open !== false;
  const closedMsg = (lang === 'ar' ? settings.store_closed_message_ar : settings.store_closed_message)
    || settings.store_closed_message
    || t('closure.cannotCheckout', 'Checkout is paused while the store is closed.');

  const totalQty = (cart.items || []).reduce((s, i) => s + i.quantity, 0);
  const discount = totalQty >= 50 ? cart.subtotal * 0.15 : totalQty >= 20 ? cart.subtotal * 0.10 : totalQty >= 10 ? cart.subtotal * 0.05 : 0;
  const shipping = cart.subtotal >= 35 ? 0 : 4.99;
  const total = cart.subtotal - discount + shipping;

  // Fire checkout_start once when the checkout page mounts with items
  useEffect(() => {
    if (cart.items && cart.items.length > 0) {
      track('checkout_start', { subtotal: cart.subtotal, item_count: totalQty });
    }
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!storeOpen) { toast.error(closedMsg); return; }
    const required = ['name', 'line1', 'city', 'zip', 'country'];
    for (const k of required) {
      if (!address[k] || !String(address[k]).trim()) { toast.error(`Please enter ${k}`); return; }
    }
    setLoading(true);
    try {
      const { data } = await api.post('/orders', { address, payment_method: 'cash_on_delivery' });
      track('checkout_complete', { order_id: data.id, total: data.total_amount, items: totalQty });
      await refresh();
      toast.success(t('checkout.orderPlacedToast', 'Order placed! Your confirmation PDF is ready. We will invoice once your order enters production.'));
      // Auto-open the order confirmation PDF for the customer's records.
      // The invoice is issued later, when the admin moves the order into
      // production.
      const opened = await openConfirmation(data.id);
      if (!opened) {
        console.warn('Confirmation tab could not be opened (likely popup blocker). Falling back to order page.');
      }
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('checkout.failed', 'Checkout failed'));
    } finally { setLoading(false); }
  };

  if (!cart.items || cart.items.length === 0) {
    return <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center" style={{ color: 'var(--jw-text-muted)' }}>{t('cart.empty', 'Your cart is empty.')}</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('checkout.title', 'Checkout')}</h1>
      <p className="mt-1 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
        {t('checkout.subtitle', 'Confirm your delivery details — no payment required now, we contact you to arrange delivery and settlement.')}
      </p>
      {!storeOpen && (
        <div
          data-testid="checkout-closure-banner"
          className="mt-4 flex items-start gap-2 rounded-lg border p-3 text-sm"
          style={{ background: 'rgba(244,63,94,0.08)', borderColor: 'rgba(244,63,94,0.35)', color: 'var(--jw-text)' }}
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-400" />
          <span><strong>{t('closure.tag', 'Store paused:')}</strong> {closedMsg}</span>
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-6">
        <form onSubmit={submit} className="space-y-6">
          <Card className="jw-card p-5">
            <h3 className="font-semibold mb-4">{t('checkout.shippingAddress', 'Shipping Address')}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.fullName', 'Full name')}</label>
                <Input data-testid="checkout-name-input" value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} className="mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.addressLine', 'Address line')}</label>
                <Input data-testid="checkout-line1-input" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="mt-1" />
              </div>
              <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.city', 'City')}</label><Input data-testid="checkout-city-input" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="mt-1" /></div>
              <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.state', 'State / Region')}</label><Input data-testid="checkout-state-input" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="mt-1" /></div>
              <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.zip', 'Postal code')}</label><Input data-testid="checkout-zip-input" value={address.zip} onChange={(e) => setAddress({ ...address, zip: e.target.value })} className="mt-1" /></div>
              <div>
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.country', 'Country')}</label>
                <Select value={address.country} onValueChange={(v) => setAddress({ ...address, country: v })}>
                  <SelectTrigger data-testid="checkout-country-select" className="mt-1"><SelectValue placeholder={t('checkout.selectCountry', 'Select country')} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`checkout-country-${c.toLowerCase().replace(/\s+/g, '-')}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.phone', 'Phone (optional)')}</label>
                <Input data-testid="checkout-phone-input" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="mt-1" />
              </div>
            </div>
          </Card>

          <Card className="jw-card p-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold">{t('checkout.paymentOnDelivery', 'Payment on delivery')}</h3>
            </div>
            <p className="text-sm mt-2" style={{ color: 'var(--jw-text-muted)' }}>
              {t('checkout.paymentNote', 'No online payment is required at this stage. After placing your order, our team will contact you to confirm the design, delivery address, and settle the amount on delivery.')}
            </p>
            <div className="mt-3 flex items-start gap-2 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
              <span>{t('checkout.sslNote', "Your data is protected with 256-bit SSL. We'll only use it to fulfill your order.")}</span>
            </div>
          </Card>

          <Button type="submit" disabled={loading || !storeOpen} data-testid="checkout-submit-button" className="jw-btn-glossy rounded-[14px] h-12 px-8 disabled:opacity-50 disabled:cursor-not-allowed">
            {!storeOpen ? t('closure.addToCartLabel', 'Orders paused') : (loading ? t('checkout.placing', 'Placing order...') : `${t('checkout.placeOrder', 'Place order')} — ${format(total)}`)}
          </Button>
        </form>

        <Card className="jw-card p-5 h-fit">
          <h3 className="font-display text-lg font-semibold">{t('checkout.summary', 'Summary')}</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span style={{ color: 'var(--jw-text-muted)' }}>{totalQty} {t('checkout.items', 'items')}</span><span>{format(cart.subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-300"><span>{t('cart.bulkDiscount', 'Bulk discount')}</span><span>- {format(discount)}</span></div>}
            <div className="flex justify-between"><span style={{ color: 'var(--jw-text-muted)' }}>{t('cart.shipping', 'Shipping')}</span><span>{shipping === 0 ? t('cart.free', 'Free') : format(shipping)}</span></div>
            <div className="border-t pt-3 flex justify-between font-semibold" style={{ borderColor: 'var(--jw-border)' }}><span>{t('cart.total', 'Total')}</span><span>{format(total)}</span></div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Checkout;
